// ═══════════════════════════════════════════════════════════════════════════════
// src/main.jsx
// Vite entry. Mounts <AuthProvider><App /></AuthProvider> into #root.
// ═══════════════════════════════════════════════════════════════════════════════

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./auth/AuthProvider";
import { ToastProvider } from "./components/ToastProvider";
import App from "./App";
import "./styles.css";
import { applyTheme, getThemePreference, watchSystemTheme } from "./lib/theme";
import { registerServiceWorker } from "./lib/pwa";
import { installChunkErrorReload } from "./lib/chunkReload";

// Before first paint: no flash of the wrong theme (UI Excellence item 7).
applyTheme(getThemePreference());
watchSystemTheme();
// A page bundle removed by a newer deploy reloads the app once instead of
// leaving a white screen (lib/chunkReload).
installChunkErrorReload();

// Installable app + portal notifications (public/sw.js). Production builds
// only; skipped under browser automation so smoke tests see the plain app.
if (import.meta.env.PROD && !navigator.webdriver) registerServiceWorker();

// Shared server-state cache (TanStack Query): lib/queryClient.js, so plain
// modules (lib/shellCounts.js) can read and dedupe through the same cache.

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <App />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
